import React, { useEffect, useRef, useState } from "react";
import {
	Alert,
	Badge,
	Button,
	Collapse,
	Input,
	message,
	Modal,
	Popconfirm,
	Popover,
	Space,
	Spin,
	Table,
	Tabs,
	Tag,
	Typography,
} from "antd";
import {
	CheckCircleOutlined,
	CloseOutlined,
	ExclamationCircleOutlined,
	WarningOutlined,
} from "@ant-design/icons";
import { useQuery } from "@tanstack/react-query";
import dayjs from "dayjs";
import { BodyMapView } from "../../../../../../components/shared/BodyModel";
import {
	getPatientAnalysisById,
	savePractitionerReview,
	signOffAnalysis,
} from "../../../../../../api/healthcare/PatientAnalysisApi";
import {
	AnalysisStatus,
	ClinicalAssessment,
	ExecutiveSummary,
	ClinicalConsideration,
	CofactorRecommendation,
	ConfirmedDiagnosis,
	ContentDecision,
	CurrentMedicationReview,
	FunctionalMarker,
	GeneticFinding,
	GeneticProfile,
	GeneticRiskFactor,
	HealthRiskClassification,
	IncidentalFinding,
	LifestyleAssessment,
	MarkerStatus,
	MedicationAnalysis,
	MedicationAppropriateness,
	MedicationDecision,
	MedicationRecommendation,
	MedicationSafetyExclusion,
	MethylationVariant,
	NarrativeClaim,
	NotRecommendedMedication,
	PatientAnalysis,
	PatientBodyMap,
	PgxPair,
	RecommendedAction,
	ReviewStatus,
} from "../../../../../../types/healthcare/PatientAnalysisTypes";
import { MedicalHistory } from "../../../../../../types/healthcare/MedicalHistoryTypes";
import { fetchPatientBodyMap } from "../../../../../../api/healthcare/BodyMapApi";
import { fetchCatalogMedication } from "../../../../../../api/healthcare/MedicationCatalogApi";
import { fetchMedicalHistory } from "../../../../../../api/healthcare/PatientApi";
import { fetchTeamMembers } from "../../../../../../api/admin/teamApi";
import { normaliseBodyGender } from "../../../../../../utils/healthcare/bodyGender";
import InvestigationsTab from "./InvestigationsTab";
import DismissibleItem, { getContentDecision } from "./DismissibleItem";
import { medicationReviewState } from "./medicationReviewState";
import EvidenceRefList from "../../../../../../components/healthcare/evidenceRefs/EvidenceRefList";

const { Text } = Typography;

export interface ComprehensiveAnalysisRendererProps {
	analysisId?: number;
	planId: number;
	onReviewed?: () => void;
	preloadedAnalysis?: PatientAnalysis;
	showReviewButton?: boolean;
	hideBodyMap?: boolean;
	/** Raw patient sex/gender value from the patient record (e.g. "male", "female", "M", "F"). */
	patientGender?: string;
}

const POLLING_STATUSES: AnalysisStatus[] = ["pending", "processing", "stage_1_complete", "appraising"];

const MARKER_STATUS_COLOR: Record<MarkerStatus, string> = {
	normal: "green",
	borderline: "orange",
	abnormal: "red",
	critical: "red",
};

const APPROPRIATENESS_COLOR: Record<MedicationAppropriateness, string> = {
	appropriate: "green",
	caution: "orange",
	contraindicated: "red",
	review_required: "gold",
};

const RECOMMENDED_ACTION_LABEL: Record<RecommendedAction, string> = {
	continue: "Continue",
	monitor: "Monitor",
	dose_adjust: "Dose Adjust",
	discontinue: "Discontinue",
	discuss_with_patient: "Discuss",
};

const REVIEW_STATUS_TAG: Record<ReviewStatus, { color: string; label: string }> = {
	awaiting_review:          { color: "gold",    label: "Awaiting Review" },
	under_review:             { color: "blue",    label: "Under Review" },
	approved:                 { color: "green",   label: "Approved" },
	approved_with_amendments: { color: "green",   label: "Approved with Amendments" },
	rejected:                 { color: "red",     label: "Rejected" },
};

const PRIORITY_COLOR: Record<"high" | "medium" | "low", string> = {
	high: "red",
	medium: "orange",
	low: "blue",
};

// Mirrors App\Enums\MedicationRedFlag::label() in healthcare — same ten keys, same wording, so
// a red-flag key rendered here reads identically to the deterministic safety screener's own
// audit log. Falls back to the raw key for a value the frontend hasn't been updated for yet.
const RED_FLAG_LABEL: Record<string, string> = {
	active_malignancy: "Active malignancy",
	pregnancy: "Pregnancy",
	breastfeeding: "Breastfeeding",
	severe_hepatic_impairment: "Severe hepatic impairment",
	severe_renal_impairment: "Severe renal impairment",
	active_systemic_infection: "Active systemic infection",
	peptide_hypersensitivity: "Peptide hypersensitivity",
	wilsons_disease: "Wilson's disease",
	active_pituitary_tumour: "Active pituitary tumour or acromegaly",
	intracranial_hypertension_or_mass: "Intracranial hypertension or known intracranial mass",
};

// Rank for sorting recommendations most → least important. "moderate" (cofactor types)
// and "medium" (lifestyle/medication types) share rank 1. Unknown priorities sort last.
const PRIORITY_RANK: Record<string, number> = { high: 0, medium: 1, moderate: 1, low: 2 };
// Returns a priority-sorted copy (high first). Array.prototype.sort is stable, so
// equal-priority items keep their original backend order. Never mutates the input.
const byPriority = <T extends { priority: string }>(items: T[]): T[] =>
	[...items].sort((a, b) => (PRIORITY_RANK[a.priority] ?? 99) - (PRIORITY_RANK[b.priority] ?? 99));

// Rank for sorting clinical considerations most -> least confident. Unknown confidences sort last.
const CONFIDENCE_RANK: Record<string, number> = { high: 0, moderate: 1, low: 2 };
// Returns a confidence-sorted copy (high first). Array.prototype.sort is stable, so
// equal-confidence items keep their original backend order. Never mutates the input.
const byConfidence = <T extends { confidence: string }>(items: T[]): T[] =>
	[...items].sort((a, b) => (CONFIDENCE_RANK[a.confidence] ?? 99) - (CONFIDENCE_RANK[b.confidence] ?? 99));

const FUNCTIONAL_IMPACT_COLOR: Record<string, string> = {
	normal:              "green",
	mildly_reduced:      "gold",
	moderately_reduced:  "orange",
	severely_reduced:    "red",
	increased:           "purple",
};

const sectionLabel: React.CSSProperties = {
	fontSize: 10,
	fontWeight: 700,
	textTransform: "uppercase",
	letterSpacing: "0.05em",
	color: "var(--mg-text-secondary)",
	display: "block",
	marginBottom: 6,
};

const panelStyle: React.CSSProperties = {
	border: "1px solid var(--mg-border)",
	borderRadius: 6,
	padding: "8px 10px",
	background: "var(--mg-bg-card, var(--mg-bg))",
};

/* ─── Shared NarrativeClaim list ────────────────────────────────────────────── */

const NarrativeClaimList: React.FC<{
	claims: NarrativeClaim[];
	icon?: React.ReactNode;
	iconColor?: string;
}> = ({ claims, icon, iconColor }) => {
	if (!claims || !Array.isArray(claims) || claims.length === 0) return null;
	return (
		<div style={{ display: "flex", flexDirection: "column", gap: 3 }}>
			{claims.map((claim, i) => (
				<div key={i} style={{ display: "flex", alignItems: "flex-start", gap: 5 }}>
					{icon && (
						<span style={{ color: iconColor, fontSize: 11, marginTop: 2, flexShrink: 0 }}>
							{icon}
						</span>
					)}
					{claim.evidence_refs && claim.evidence_refs.length > 0 ? (
						<Popover content={<EvidenceRefList refs={claim.evidence_refs} />} trigger="hover">
							<span style={{ fontSize: 11, cursor: "default" }}>
								{claim.statement}
							</span>
						</Popover>
					) : (
						<Text style={{ fontSize: 11, flex: 1 }}>{claim.statement}</Text>
					)}
				</div>
			))}
		</div>
	);
};

/* ─── Shared dismissible NarrativeClaim list ────────────────────────────────── */

// Same rendering as NarrativeClaimList (icon + evidence-ref popover), but wraps each claim
// in DismissibleItem so it can be individually dismissed/edited. Kept separate from
// NarrativeClaimList (rather than adding dismiss support to it directly) because that
// component has other callers that don't carry decision state (e.g. DiagnosesRisksTab's
// `supporting_evidence`, which is out of scope for this stage).
const DismissibleNarrativeClaimList: React.FC<{
	claims: NarrativeClaim[];
	sectionName: string;
	decisions: ContentDecision[];
	onChange: (itemKey: string, patch: Partial<ContentDecision>) => void;
	isLocked: boolean;
	icon?: React.ReactNode;
	iconColor?: string;
}> = ({ claims, sectionName, decisions, onChange, isLocked, icon, iconColor }) => {
	if (!claims || !Array.isArray(claims) || claims.length === 0) return null;
	return (
		<div style={{ display: "flex", flexDirection: "column", gap: 3 }}>
			{claims.map((claim, i) => (
				<DismissibleItem
					key={i}
					itemKey={`${sectionName}:${i}`}
					decisions={decisions}
					onChange={onChange}
					isLocked={isLocked}
					editable
					defaultText={claim.statement}
				>
					{(effectiveText) => (
						<div style={{ display: "flex", alignItems: "flex-start", gap: 5 }}>
							{icon && (
								<span style={{ color: iconColor, fontSize: 11, marginTop: 2, flexShrink: 0 }}>
									{icon}
								</span>
							)}
							{claim.evidence_refs && claim.evidence_refs.length > 0 ? (
								<Popover content={<EvidenceRefList refs={claim.evidence_refs} />} trigger="hover">
									<span style={{ fontSize: 11, cursor: "default" }}>
										{effectiveText}
									</span>
								</Popover>
							) : (
								<Text style={{ fontSize: 11, flex: 1 }}>{effectiveText}</Text>
							)}
						</div>
					)}
				</DismissibleItem>
			))}
		</div>
	);
};

/* ─── Processing / failed states ───────────────────────────────────────────── */

const ProcessingState: React.FC<{ message?: string }> = ({ message }) => (
	<div style={{ display: "flex", alignItems: "center", gap: 8, padding: "12px 0", color: "var(--mg-text-secondary)" }}>
		<Spin size="small" />
		<Text style={{ fontSize: 12, color: "var(--mg-text-secondary)" }}>
			{message ?? "Generating comprehensive analysis — may take up to 15 minutes"}
		</Text>
	</div>
);

const FailedState: React.FC<{ message: string | null }> = ({ message: msg }) => (
	<Text style={{ fontSize: 12, color: "var(--mg-error, #ff4d4f)", display: "block", padding: "8px 0" }}>
		Analysis failed: {msg ?? "Unknown error"}
	</Text>
);

/* ─── Genetic tab ───────────────────────────────────────────────────────────── */

const methylationVariantColumns = [
	{ title: "Gene", dataIndex: "gene", key: "gene", width: 80, render: (v: string) => <Text style={{ fontSize: 11, fontWeight: 600 }}>{v}</Text> },
	{ title: "Variant", dataIndex: "variant", key: "variant", width: 100, render: (v: string) => <Text style={{ fontSize: 11 }}>{v}</Text> },
	{ title: "Genotype", dataIndex: "genotype", key: "genotype", width: 90, render: (v: string) => <Text style={{ fontSize: 11, fontFamily: "monospace" }}>{v}</Text> },
	{
		title: "Functional Impact",
		dataIndex: "predicted_functional_impact",
		key: "predicted_functional_impact",
		render: (v: string) => (
			<Tag color={FUNCTIONAL_IMPACT_COLOR[v] ?? "default"} style={{ margin: 0, fontSize: 10 }}>
				{v.replace(/_/g, " ")}
			</Tag>
		),
	},
];

const functionalMarkerColumns = [
	{ title: "Marker", dataIndex: "marker", key: "marker", render: (v: string) => <Text style={{ fontSize: 11 }}>{v}</Text> },
	{ title: "Value", dataIndex: "value", key: "value", width: 80, render: (v: string) => <Text style={{ fontSize: 11 }}>{v}</Text> },
	{ title: "Unit", dataIndex: "unit", key: "unit", width: 60, render: (v: string) => <Text style={{ fontSize: 10, color: "var(--mg-text-secondary)" }}>{v}</Text> },
	{ title: "Range", dataIndex: "reference_range", key: "reference_range", width: 100, render: (v: string) => <Text style={{ fontSize: 10 }}>{v}</Text> },
	{
		title: "Status",
		dataIndex: "status",
		key: "status",
		width: 90,
		render: (v: MarkerStatus) => <Badge color={MARKER_STATUS_COLOR[v]} text={<Text style={{ fontSize: 10 }}>{v}</Text>} />,
	},
];

const geneticFindingColumns = [
	{
		title: "Category",
		dataIndex: "category",
		key: "category",
		width: 120,
		render: (v: string) => <Tag style={{ margin: 0, fontSize: 10 }}>{v}</Tag>,
	},
	{
		title: "Genes",
		dataIndex: "genes",
		key: "genes",
		render: (genes: string[]) => <Text style={{ fontSize: 11 }}>{genes.join(", ")}</Text>,
	},
	{
		title: "Finding",
		dataIndex: "finding",
		key: "finding",
		render: (v: string) => <Text style={{ fontSize: 11 }}>{v}</Text>,
	},
	{
		title: "Severity",
		dataIndex: "severity",
		key: "severity",
		width: 90,
		render: (v: string) => {
			const color = v === "critical" ? "red" : v === "warning" ? "orange" : v === "info" ? "blue" : "green";
			return <Tag color={color} style={{ margin: 0, fontSize: 10 }}>{v}</Tag>;
		},
	},
	{
		title: "Evidence",
		dataIndex: "evidence_level",
		key: "evidence_level",
		width: 90,
		render: (v: string) => {
			const color = v === "strong" ? "green" : v === "moderate" ? "orange" : "default";
			return <Tag color={color} style={{ margin: 0, fontSize: 10 }}>{v}</Tag>;
		},
	},
];

const GeneticTab: React.FC<{
	profile: GeneticProfile;
	decisions: ContentDecision[];
	onChange: (itemKey: string, patch: Partial<ContentDecision>) => void;
	isLocked: boolean;
}> = ({ profile, decisions, onChange, isLocked }) => {
	const meth = profile.methylation_assessment;
	return (
		<div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
			{profile.summary && profile.summary.length > 0 && (
				<div style={panelStyle}>
					<Text style={sectionLabel}>Genetic Summary</Text>
					<DismissibleNarrativeClaimList
						claims={profile.summary}
						sectionName="summary"
						decisions={decisions}
						onChange={onChange}
						isLocked={isLocked}
					/>
				</div>
			)}

			{profile.conventional_genetic_summary && profile.conventional_genetic_summary.length > 0 && (
				<div style={panelStyle}>
					<Text style={sectionLabel}>Conventional Genetic Summary</Text>
					<DismissibleNarrativeClaimList
						claims={profile.conventional_genetic_summary}
						sectionName="conventional_summary"
						decisions={decisions}
						onChange={onChange}
						isLocked={isLocked}
					/>
				</div>
			)}

			{meth && (
				<div style={panelStyle}>
					<Text style={sectionLabel}>Methylation Assessment</Text>

					{meth.variants.length > 0 && (
						<>
							<Text style={{ ...sectionLabel, marginTop: 4 }}>Variants</Text>
							<Table<MethylationVariant>
								dataSource={meth.variants}
								columns={methylationVariantColumns}
								rowKey={(r) => `${r.gene}-${r.variant}`}
								size="small"
								pagination={false}
								style={{ marginBottom: 10 }}
							/>
						</>
					)}

					{meth.functional_markers.length > 0 && (
						<>
							<Text style={{ ...sectionLabel, marginTop: 4 }}>Functional Markers</Text>
							<Table<FunctionalMarker>
								dataSource={meth.functional_markers}
								columns={functionalMarkerColumns}
								rowKey={(r) => r.marker}
								size="small"
								pagination={false}
								style={{ marginBottom: 10 }}
							/>
						</>
					)}

					{meth.cofactor_recommendations.length > 0 && (
						<>
							<Text style={{ ...sectionLabel, marginTop: 4 }}>Cofactor Recommendations</Text>
							<div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
								{byPriority(meth.cofactor_recommendations).map((c: CofactorRecommendation) => {
									// item_key must stay stable across the priority re-sort — derive it from
									// the position in the original (unsorted) array, not the sorted map index.
									// byPriority only shallow-copies the array, so referential equality holds.
									const originalIndex = meth.cofactor_recommendations.indexOf(c);
									return (
										<DismissibleItem
											key={originalIndex}
											itemKey={`cofactor:${originalIndex}`}
											decisions={decisions}
											onChange={onChange}
											isLocked={isLocked}
											editable
											defaultText={c.rationale.statement}
										>
											{(effectiveText) => (
												<div style={{ display: "flex", alignItems: "flex-start", gap: 5 }}>
													<Tag color={c.priority === "high" ? "red" : c.priority === "moderate" ? "orange" : "blue"} style={{ margin: 0, fontSize: 10, flexShrink: 0 }}>
														{c.priority}
													</Tag>
													<Text style={{ fontSize: 11, fontWeight: 600, flexShrink: 0 }}>{c.cofactor}</Text>
													{c.rationale.evidence_refs && c.rationale.evidence_refs.length > 0 ? (
														<Popover content={<EvidenceRefList refs={c.rationale.evidence_refs} />} trigger="hover">
															<span style={{ fontSize: 11, color: "var(--mg-text-secondary)", cursor: "default" }}>
																{effectiveText}
															</span>
														</Popover>
													) : (
														<Text style={{ fontSize: 11, color: "var(--mg-text-secondary)", flex: 1 }}>{effectiveText}</Text>
													)}
												</div>
											)}
										</DismissibleItem>
									);
								})}
							</div>
						</>
					)}

					{meth.clinical_interpretation.length > 0 && (
						<>
							<Text style={{ ...sectionLabel, marginTop: 8 }}>Clinical Interpretation</Text>
							<DismissibleNarrativeClaimList
								claims={meth.clinical_interpretation}
								sectionName="clinical_interpretation"
								decisions={decisions}
								onChange={onChange}
								isLocked={isLocked}
							/>
						</>
					)}
				</div>
			)}

			{profile.key_findings.length > 0 && (
				<div style={panelStyle}>
					<Text style={sectionLabel}>Key Findings</Text>
					<Table<GeneticFinding>
						dataSource={profile.key_findings}
						columns={geneticFindingColumns}
						rowKey={(r) => r.finding}
						size="small"
						scroll={{ x: true }}
						pagination={false}
						expandable={{
							expandedRowRender: (r) => (
								<Text style={{ fontSize: 11, color: "var(--mg-text-secondary)", display: "block", padding: "2px 0" }}>
									{r.clinical_significance}
								</Text>
							),
						}}
					/>
				</div>
			)}
		</div>
	);
};

/* ─── Diagnoses & Risks tab ─────────────────────────────────────────────────── */


const riskFactorColumns = [
	{ title: "Risk", dataIndex: "risk_description", key: "risk_description", render: (v: string) => <Text style={{ fontSize: 11 }}>{v}</Text> },
	{ title: "Gene", dataIndex: "gene", key: "gene", width: 80, render: (v: string) => <Text style={{ fontSize: 11, fontWeight: 600 }}>{v}</Text> },
	{ title: "Variant", dataIndex: "variant", key: "variant", width: 90, render: (v: string | null) => v ? <Text style={{ fontSize: 10, fontFamily: "monospace" }}>{v}</Text> : null },
	{
		title: "Actionability",
		dataIndex: "actionability",
		key: "actionability",
		width: 110,
		render: (v: string) => {
			const color = v === "high" ? "red" : v === "moderate" ? "orange" : v === "low" ? "blue" : "default";
			return <Tag color={color} style={{ margin: 0, fontSize: 10 }}>{v}</Tag>;
		},
	},
	{
		title: "Evidence",
		dataIndex: "evidence_level",
		key: "evidence_level",
		width: 90,
		render: (v: string) => {
			const color = v === "strong" ? "green" : v === "moderate" ? "orange" : "default";
			return <Tag color={color} style={{ margin: 0, fontSize: 10 }}>{v}</Tag>;
		},
	},
];

// MD's SIDE code, as parsed at MedicalDirectorXmlParser.php:726. An unrecognised code is
// rendered verbatim rather than dropped — the record says what it says.
const SOURCE_SIDE_LABEL: Record<string, string> = {
	B: "bilateral",
	L: "left",
	R: "right",
};

// Which import produced the source record. `details.source` is a literal the importer writes
// (MedicalHistoryImporter.php:53), so it is read rather than a provenance line hardcoded here.
const SOURCE_SYSTEM_LABEL: Record<string, string> = {
	md_import: "Medical Director import",
};

// Provenance dates arrive as a mix of a Y-m-d string in `details` and datetime columns.
// Returns null — never a placeholder — when the value is absent or unparseable: a diagnosis
// with no recorded date shows no date at all (Harry, answer 3: no "unknown", no em-dash), and
// "Invalid Date" must never reach a practitioner.
const formatSourceDate = (value: string | Date | null | undefined): string | null => {
	if (!value) return null;
	const parsed = dayjs(value as string);
	return parsed.isValid() ? parsed.format("D MMM YYYY") : null;
};

const joinName = (parts: (string | null | undefined)[]): string | null => {
	const name = parts.filter((p) => p != null && String(p).trim() !== "").join(" ").trim();
	return name === "" ? null : name;
};

// The `details` keys MedicalHistoryImporter writes (:52-79). All optional: the importer's
// array_filter drops nulls and empty strings, so an ABSENT key means "the export did not say"
// — which is why the panel renders nothing for it rather than a placeholder.
interface SourceRecordDetails {
	source?: string;
	notes?: string;
	side?: string;
	histcode?: string;
	provider_dr_no?: string;
	provider_name?: string;
	recorded_date?: string;
	is_significant?: boolean;
	measure_type?: string;
	result_value?: string;
	measure_details?: string;
}

// The eager-loaded relation MedicalHistoryController::show() returns (86d3vny73). Declared
// locally rather than widened onto the shared MedicalHistory type — a dozen unrelated screens
// consume that type and none of them read provenance.
type SourceRecord = MedicalHistory & {
	details?: SourceRecordDetails;
	// The clinician's MD <HIDE> flag. Absent from the shared MedicalHistory type, and show()
	// deliberately does not filter on it (scopeVisible's docblock: a practitioner addressing a
	// record by id must still reach it to edit it), so a reader has to check it itself.
	is_hidden?: boolean | null;
	external_practitioner?: {
		title?: string | null;
		first_name?: string | null;
		last_name?: string | null;
	} | null;
};

const SourceRecordRow: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
	<div style={{ display: "flex", gap: 6 }}>
		<Text style={{ fontSize: 11, color: "var(--mg-text-secondary)", flexShrink: 0, minWidth: 92 }}>{label}</Text>
		<Text style={{ fontSize: 11 }}>{children}</Text>
	</div>
);

// Why the panel declined to render a row it did fetch. Visible rather than a bare null: a silent
// nothing is indistinguishable from "this analysis predates S10", and each reason states the
// truthful one rather than a single generic line covering both.
const SourceRecordRefusal: React.FC<{ reason: string }> = ({ reason }) => (
	<Text style={{ fontSize: 11, color: "var(--mg-text-secondary)", display: "block", marginTop: 8, fontStyle: "italic" }}>
		{reason}
	</Text>
);

/**
 * Read-only provenance for the imported record a confirmed diagnosis was derived from — the
 * recording clinician, the recorded and onset dates, and the rest of what the MD3 export
 * preserved. Kept visually separate from the AI-authored definition above it: this is chart
 * data the practitioner's own system recorded, not analysis.
 *
 * Fetched lazily. antd's Collapse does not mount an inactive panel's children, so mounting the
 * query here rather than in DiagnosesRisksTab means opening the tab fires no requests — only
 * expanding a diagnosis does.
 *
 * 🚨 `source_record_id` IS UNTRUSTED INPUT, NOT A LINK — THE OWNERSHIP CHECK BELOW IS LOAD-BEARING.
 * MedicalHistoryController::show() is a bare findOrFail with no patient scoping, and on the
 * section-appraisal path nothing reconciles this id against a real row (the appraisal model is
 * handed formatted prompt TEXT, not records, so the server cannot confirm what it emits). A
 * model-invented or mistyped id can therefore reach the stored analysis, and fetching one
 * blindly would render a DIFFERENT patient's medical history — clinician-authored
 * `details.notes` free text included. The row is checked against the analysis' own patient and
 * the panel refuses to render on a mismatch. Do not remove that check, and do not "fix" it by
 * widening show()'s authorisation instead — that endpoint has other consumers (useReferenceModal)
 * and securing it properly is its own decision.
 *
 * `details.notes` is fine to render to the treating practitioner and must never be logged.
 */
const DiagnosisSourcePanel: React.FC<{ sourceRecordId: number; patientId: string }> = ({ sourceRecordId, patientId }) => {
	const { data, isError } = useQuery({
		queryKey: ["medicalHistory", sourceRecordId],
		queryFn: () => fetchMedicalHistory(sourceRecordId),
	});
	const record = data as SourceRecord | undefined;

	// The internal-clinician name tier. `primary_provider_id` is an AUTH-service user id and
	// healthcare has no users table, so it can only be resolved here. Gated on the row actually
	// carrying one, so an externally-recorded or MD-name-only row fires no staff lookup.
	const { data: team } = useQuery({
		queryKey: ["teamMembers", "diagnosis-source-provider"],
		queryFn: () => fetchTeamMembers(1, 200),
		enabled: record?.primary_provider_id != null,
		staleTime: 5 * 60 * 1000,
	});

	// Still loading, or the lookup failed: render nothing. A provenance fetch must never break
	// the review — the AI definition above renders regardless.
	if (isError || !record) return null;

	// See the security note above. Refuse, visibly — never silently show a mismatched record.
	//
	// ⚠️ THE ABSENT-ID CASES REFUSE TOO, AND THAT IS NOT DEFENSIVE PADDING. Both columns are
	// NOT NULL and non-optional in TypeScript, so `String(undefined) !== String(undefined)`
	// looks unreachable — but the whole premise of this check is that it must not depend on the
	// types being honest, and `source_record_id` arriving here unvalidated is proof that they
	// sometimes are not. A comparison of two missing ids must never be read as a match.
	if (!patientId || record.patient_id == null || String(record.patient_id) !== String(patientId)) {
		return <SourceRecordRefusal reason="Source record could not be verified against this patient — not shown." />;
	}

	// The clinician hid this row at the source (MD <HIDE>, 86d3p77kn). show() deliberately does
	// not apply visible() — a practitioner addressing a record by id must still reach it to edit
	// it — so the filter has to happen at the reader. DEFENCE IN DEPTH, not a workaround:
	// MedicalHistoryDataSource DOES apply visible(), so a hidden row should never have carried a
	// source_record_id into an analysis in the first place, and one that did is a data problem
	// worth investigating rather than something this branch makes acceptable.
	if (record.is_hidden === true) {
		return <SourceRecordRefusal reason="Source record was hidden in the patient's chart — not shown." />;
	}

	const details: SourceRecordDetails = record.details ?? {};
	const internalMember = team?.data.find((m) => m.id === record.primary_provider_id);
	// Name resolution order: internal user -> external practitioner -> the MD-authored string.
	const recordedBy =
		joinName([internalMember?.first_name, internalMember?.last_name])
		?? joinName([
			record.external_practitioner?.title,
			record.external_practitioner?.first_name,
			record.external_practitioner?.last_name,
		])
		?? joinName([details.provider_name]);
	const drNo = details.provider_dr_no;
	const recordedDate = formatSourceDate(details.recorded_date);
	const onsetDate = formatSourceDate(record.date_identified);
	const resolvedDate = formatSourceDate(record.date_resolved);
	const side = details.side ? SOURCE_SIDE_LABEL[details.side] ?? details.side : null;
	const sourceSystem = details.source ? SOURCE_SYSTEM_LABEL[details.source] ?? details.source : null;

	return (
		<div style={{ marginTop: 8, paddingTop: 6, borderTop: "1px solid var(--mg-border)" }}>
			<Text style={sectionLabel}>Source Record</Text>
			<div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
				{(recordedBy || drNo) && (
					<SourceRecordRow label="Recorded by">
						{recordedBy ?? "Not named"}{drNo ? ` · DR_NO ${drNo}` : ""}
					</SourceRecordRow>
				)}
				{recordedDate && <SourceRecordRow label="Recorded">{recordedDate}</SourceRecordRow>}
				{onsetDate && <SourceRecordRow label="Onset">{onsetDate}</SourceRecordRow>}
				{resolvedDate && <SourceRecordRow label="Resolved">{resolvedDate}</SourceRecordRow>}
				{record.description && <SourceRecordRow label="As recorded">{record.description}</SourceRecordRow>}
				{details.histcode && <SourceRecordRow label="Histcode">{details.histcode}</SourceRecordRow>}
				{side && <SourceRecordRow label="Side">{side}</SourceRecordRow>}
				{details.measure_type && <SourceRecordRow label="Measure">{details.measure_type}</SourceRecordRow>}
				{details.result_value && <SourceRecordRow label="Result">{details.result_value}</SourceRecordRow>}
				{details.measure_details && <SourceRecordRow label="Measure detail">{details.measure_details}</SourceRecordRow>}
				{details.notes && <SourceRecordRow label="Notes">{details.notes}</SourceRecordRow>}
				{details.is_significant && (
					<SourceRecordRow label="Flagged">
						<Tag color="blue" style={{ margin: 0, fontSize: 10 }}>significant / front sheet</Tag>
					</SourceRecordRow>
				)}
				{sourceSystem && <SourceRecordRow label="Source">{sourceSystem}</SourceRecordRow>}
			</div>
		</div>
	);
};

const DiagnosesRisksTab: React.FC<{
	confirmedDiagnoses: ConfirmedDiagnosis[];
	clinicalConsiderations: ClinicalConsideration[];
	geneticRiskFactors: GeneticRiskFactor[];
	patientId: string;
	decisions: ContentDecision[];
	onChange: (itemKey: string, patch: Partial<ContentDecision>) => void;
	isLocked: boolean;
}> = ({ confirmedDiagnoses, clinicalConsiderations, geneticRiskFactors, patientId, decisions, onChange, isLocked }) => (
	<div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
		<div style={panelStyle}>
			<Text style={sectionLabel}>Confirmed Diagnoses</Text>
			{confirmedDiagnoses.length === 0 ? (
				<Text style={{ fontSize: 11, color: "var(--mg-text-secondary)" }}>None recorded</Text>
			) : (
				<Collapse
					ghost
					size="small"
					items={confirmedDiagnoses.map((d, i) => {
						const dateDiagnosed = formatSourceDate(d.date_diagnosed);
						return {
							key: String(i),
							label: (
								<div style={{ display: "flex", alignItems: "center", gap: 6 }}>
									<Text style={{ fontSize: 12 }}>{d.condition}</Text>
									{d.icd10_am_code && (
										<Text style={{ fontSize: 10, fontFamily: "monospace", color: "var(--mg-text-secondary)" }}>
											{d.icd10_am_code}
										</Text>
									)}
									{/* Date of diagnosis in place of the old status tag, and NOTHING when there
									    is no date — no "unknown", no em-dash, no placeholder (Harry, answer 3). */}
									{dateDiagnosed && (
										<Text style={{ fontSize: 10, color: "var(--mg-text-secondary)" }}>
											{dateDiagnosed}
										</Text>
									)}
									{/* A tag only for the two clinically load-bearing states. `active` and `unknown`
									    are noise and are gone from the UI. ⚠️ The `status` VALUE is untouched in the
									    data — MedicationSafetyScreener reads `in_remission`/`resolved` server-side as
									    the escape hatch that lifts an ambiguous confirmed-cancer suppression. */}
									{(d.status === "resolved" || d.status === "in_remission") && (
										<Tag color={d.status === "resolved" ? "green" : "gold"} style={{ margin: 0, fontSize: 10 }}>
											{d.status.replace(/_/g, " ")}
										</Tag>
									)}
								</div>
							),
							children: (
								<>
									<DismissibleItem
										itemKey={`diagnosis:${i}`}
										decisions={decisions}
										onChange={onChange}
										isLocked={isLocked}
										editable
										defaultText={d.definition}
									>
										{(effectiveText) => (
											<Text style={{ fontSize: 11, color: "var(--mg-text-secondary)", display: "block", lineHeight: 1.5 }}>
												{effectiveText}
											</Text>
										)}
									</DismissibleItem>
									{/* An analysis generated before S10 carries no source_record_id: the definition
									    renders alone, with no panel and no error. Deliberately NOT matched by
									    condition text as a fallback — the wrong source record attached to a
									    diagnosis is worse than none. */}
									{d.source_record_id != null && (
										<DiagnosisSourcePanel sourceRecordId={d.source_record_id} patientId={patientId} />
									)}
								</>
							),
						};
					})}
				/>
			)}
		</div>

		{clinicalConsiderations.length > 0 && (
			<div style={panelStyle}>
				<Text style={sectionLabel}>Clinical Considerations</Text>
				<div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
					{byConfidence(clinicalConsiderations).map((c) => {
						// item_key must stay stable across the confidence re-sort — derive it from
						// the position in the original (unsorted) array, not the sorted map index.
						// byConfidence only shallow-copies the array, so referential equality holds.
						const originalIndex = clinicalConsiderations.indexOf(c);
						return (
							<DismissibleItem
								key={originalIndex}
								itemKey={`consideration:${originalIndex}`}
								decisions={decisions}
								onChange={onChange}
								isLocked={isLocked}
								editable
								defaultText={c.definition}
							>
								{(effectiveText) => (
									<div style={{ border: "1px solid var(--mg-border)", borderRadius: 4, padding: "6px 8px" }}>
										<div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 4 }}>
											<Tag
												color={c.confidence === "high" ? "blue" : c.confidence === "moderate" ? "gold" : "default"}
												style={{ margin: 0, fontSize: 10 }}
											>
												{c.confidence} confidence
											</Tag>
											<Text style={{ fontSize: 12, fontWeight: 600 }}>{c.consideration}</Text>
											{c.icd10_am_code && (
												<Text style={{ fontSize: 10, fontFamily: "monospace", color: "var(--mg-text-secondary)" }}>
													{c.icd10_am_code}
												</Text>
											)}
										</div>
										{effectiveText && (
											<div style={{ marginBottom: 6 }}>
												<Text style={{ fontSize: 11, color: "var(--mg-text-secondary)", display: "block", fontStyle: "italic" }}>
													{effectiveText}
												</Text>
											</div>
										)}
										{c.supporting_evidence.length > 0 && (
											<div style={{ marginBottom: 4 }}>
												<Text style={{ ...sectionLabel, marginBottom: 3 }}>Supporting Evidence</Text>
												<NarrativeClaimList claims={c.supporting_evidence} />
											</div>
										)}
										{c.differential.length > 0 && (
											<div style={{ marginBottom: 3 }}>
												<Text style={{ fontSize: 10, color: "var(--mg-text-secondary)" }}>Differentials: </Text>
												<Text style={{ fontSize: 10 }}>{c.differential.join(" · ")}</Text>
											</div>
										)}
										{c.suggested_workup.length > 0 && (
											<div>
												<Text style={{ fontSize: 10, color: "var(--mg-text-secondary)" }}>Suggested workup: </Text>
												<Text style={{ fontSize: 10 }}>{c.suggested_workup.join(", ")}</Text>
											</div>
										)}
									</div>
								)}
							</DismissibleItem>
						);
					})}
				</div>
			</div>
		)}

		{geneticRiskFactors.length > 0 && (
			<div style={panelStyle}>
				<Text style={sectionLabel}>Genetic Risk Factors</Text>
				<Table<GeneticRiskFactor>
					dataSource={geneticRiskFactors}
					columns={riskFactorColumns}
					rowKey={(r) => `${r.gene}-${r.risk_description}`}
					size="small"
					scroll={{ x: true }}
					pagination={false}
				/>
			</div>
		)}
	</div>
);

/* ─── Clinical tab ──────────────────────────────────────────────────────────── */

// `analysis_output` is a free-form json column with no migration behind it, so an
// analysis written by an older schema can be missing whole sections. This tab (like
// Lifestyle) always renders, so it must tolerate that — an absent section shows as
// "None recorded" rather than white-screening the whole patient modal.
const ClinicalTab: React.FC<{
	assessment?: ClinicalAssessment;
	executive?: ExecutiveSummary;
	decisions: ContentDecision[];
	onChange: (itemKey: string, patch: Partial<ContentDecision>) => void;
	isLocked: boolean;
}> = ({ assessment, executive, decisions, onChange, isLocked }) => {
	const sections: { label: string; items: string[]; danger?: boolean }[] = [
		{ label: "Allergies", items: assessment?.allergies ?? [], danger: true },
		{ label: "Surgical History", items: assessment?.surgical_history ?? [] },
		{ label: "Family History Risks", items: assessment?.family_history_risks ?? [] },
	];

	// Executive summary content (moved here from the standalone pre-tab block).
	const narrative = executive?.clinical_narrative ?? [];
	const concerns = executive?.primary_concerns ?? [];
	const positives = executive?.positive_indicators ?? [];
	const hasNarrative = narrative.length > 0;
	const hasConcerns = concerns.length > 0;
	const hasPositives = positives.length > 0;

	return (
		<div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
			{(hasNarrative || hasConcerns || hasPositives) && (
				<div style={panelStyle}>
					{hasNarrative && (
						<div style={{ marginBottom: 8 }}>
							<Text style={sectionLabel}>Clinical Narrative</Text>
							<DismissibleNarrativeClaimList
								claims={narrative}
								sectionName="clinical_narrative"
								decisions={decisions}
								onChange={onChange}
								isLocked={isLocked}
							/>
						</div>
					)}
					{(hasConcerns || hasPositives) && (
						<div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
							{hasConcerns && (
								<div>
									<Text style={sectionLabel}>Primary Concerns</Text>
									<DismissibleNarrativeClaimList
										claims={concerns}
										sectionName="primary_concerns"
										decisions={decisions}
										onChange={onChange}
										isLocked={isLocked}
										icon={<ExclamationCircleOutlined />}
										iconColor="var(--mg-error, #ff4d4f)"
									/>
								</div>
							)}
							{hasPositives && (
								<div>
									<Text style={sectionLabel}>Positive Indicators</Text>
									<DismissibleNarrativeClaimList
										claims={positives}
										sectionName="positive_indicators"
										decisions={decisions}
										onChange={onChange}
										isLocked={isLocked}
										icon={<CheckCircleOutlined />}
										iconColor="var(--mg-success, #52c41a)"
									/>
								</div>
							)}
						</div>
					)}
				</div>
			)}
			<div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 6 }}>
				{sections.map(({ label, items, danger }) => (
					<div key={label} style={panelStyle}>
						<Text style={sectionLabel}>{label}</Text>
						{items.length === 0 ? (
							<Text style={{ fontSize: 11, color: "var(--mg-text-secondary)" }}>None recorded</Text>
						) : (
							<div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
								{items.map((item, i) => (
									<DismissibleItem
										key={i}
										itemKey={`${label}:${i}`}
										decisions={decisions}
										onChange={onChange}
										isLocked={isLocked}
										defaultText={item}
									>
										{(effectiveText) => (
											<Text style={{ fontSize: 11, color: danger ? "var(--mg-warning, #fa8c16)" : "var(--mg-text)", display: "block" }}>
												{effectiveText}
											</Text>
										)}
									</DismissibleItem>
								))}
							</div>
						)}
					</div>
				))}
			</div>
		</div>
	);
};


/* ─── Lifestyle tab ─────────────────────────────────────────────────────────── */

// Like ClinicalTab, this tab always renders, so it must survive an older
// analysis_output that has no lifestyle_assessment section.
const LifestyleTab: React.FC<{
	lifestyle?: LifestyleAssessment;
	decisions: ContentDecision[];
	onChange: (itemKey: string, patch: Partial<ContentDecision>) => void;
	isLocked: boolean;
}> = ({ lifestyle, decisions, onChange, isLocked }) => (
	<div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
		<div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 6 }}>
			<div style={panelStyle}>
				<Text style={sectionLabel}>Diet</Text>
				<Text style={{ fontSize: 11, display: "block", lineHeight: 1.5 }}>{lifestyle?.diet_score_summary ?? "Not available"}</Text>
			</div>
			<div style={panelStyle}>
				<Text style={sectionLabel}>Exercise</Text>
				<Text style={{ fontSize: 11, display: "block", lineHeight: 1.5 }}>{lifestyle?.exercise_score_summary ?? "Not available"}</Text>
			</div>
		</div>
		<div style={panelStyle}>
			<Text style={sectionLabel}>Recommendations</Text>
			<div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
				{byPriority(lifestyle?.recommendations ?? []).map((item) => {
					// item_key must stay stable across the priority re-sort — derive it from the
					// item's position in the ORIGINAL (unsorted) array, not the sorted map index,
					// otherwise dismiss/edit state attaches to the wrong item after a re-sort.
					// byPriority only shallow-copies the array (Array.prototype.sort on a spread),
					// so the objects keep referential identity and indexOf reliably finds the
					// original position.
					const originalIndex = (lifestyle?.recommendations ?? []).indexOf(item);
					return (
						<div key={originalIndex} style={{ display: "flex", flexDirection: "column", gap: 2 }}>
							<div style={{ display: "flex", alignItems: "center", gap: 5 }}>
								<Tag color={PRIORITY_COLOR[item.priority]} style={{ margin: 0, fontSize: 10, padding: "0 4px" }}>{item.priority}</Tag>
								<Tag style={{ margin: 0, fontSize: 10, padding: "0 4px" }}>{item.category}</Tag>
								<DismissibleItem
									itemKey={`recommendation:${originalIndex}`}
									decisions={decisions}
									onChange={onChange}
									isLocked={isLocked}
									editable
									defaultText={item.recommendation}
								>
									{/*
									 * `addresses` (schema 5.10.0+) names the symptom, condition or disease this
									 * recommendation targets, rendered as an emphasised prefix ahead of the text.
									 * It sits INSIDE the render prop deliberately: a dismissed or mid-edit item
									 * must not leave the label stranded on its own. It is ABSENT on every analysis
									 * stored before 5.10.0, in which case nothing is rendered at all — no label,
									 * no separator, no placeholder.
									 */}
									{(effectiveText) => (
										<Text style={{ fontSize: 12 }}>
											{item.addresses ? <Text strong>{item.addresses}: </Text> : null}
											{effectiveText}
										</Text>
									)}
								</DismissibleItem>
							</div>
							<div style={{ display: "flex", alignItems: "flex-start", gap: 5, marginLeft: 0 }}>
								{item.rationale.evidence_refs && item.rationale.evidence_refs.length > 0 ? (
									<Popover content={<EvidenceRefList refs={item.rationale.evidence_refs} />} trigger="hover">
										<span style={{ fontSize: 11, color: "var(--mg-text-secondary)", cursor: "default" }}>
											{item.rationale.statement}
										</span>
									</Popover>
								) : (
									<Text style={{ fontSize: 11, color: "var(--mg-text-secondary)", flex: 1 }}>
										{item.rationale.statement}
									</Text>
								)}
							</div>
							{item.genetic_basis && (
								<div style={{ display: "flex", alignItems: "flex-start", gap: 5 }}>
									{item.genetic_basis.evidence_refs && item.genetic_basis.evidence_refs.length > 0 ? (
										<Popover content={<EvidenceRefList refs={item.genetic_basis.evidence_refs} />} trigger="hover">
											<span style={{ fontSize: 10, color: "var(--mg-text-secondary)", fontStyle: "italic", cursor: "default" }}>
												{item.genetic_basis.statement}
											</span>
										</Popover>
									) : (
										<Text style={{ fontSize: 10, color: "var(--mg-text-secondary)", fontStyle: "italic", flex: 1 }}>
											{item.genetic_basis.statement}
										</Text>
									)}
								</div>
							)}
						</div>
					);
				})}
			</div>
		</div>
	</div>
);

/* ─── Incidental findings & limitations tab ─────────────────────────────────── */

const IncidentalTab: React.FC<{
	incidentalFindings: IncidentalFinding[];
	analysisLimitations: string[];
	decisions: ContentDecision[];
	onChange: (itemKey: string, patch: Partial<ContentDecision>) => void;
	isLocked: boolean;
}> = ({ incidentalFindings, analysisLimitations, decisions, onChange, isLocked }) => (
	<div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
		{incidentalFindings.length > 0 && (
			<div style={panelStyle}>
				<Text style={sectionLabel}>Incidental Findings</Text>
				<div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
					{incidentalFindings.map((f, i) => (
						<div key={i} style={{ border: "1px solid var(--mg-border)", borderRadius: 4, padding: "5px 8px" }}>
							<div style={{ display: "flex", alignItems: "center", gap: 5, marginBottom: 3 }}>
								<Tag
									color={f.severity === "critical" ? "red" : f.severity === "warning" ? "orange" : "blue"}
									style={{ margin: 0, fontSize: 10 }}
								>
									{f.severity}
								</Tag>
								<DismissibleItem
									itemKey={`incidental_findings:${i}`}
									decisions={decisions}
									onChange={onChange}
									isLocked={isLocked}
									editable
									defaultText={f.finding.statement}
								>
									{(effectiveText) => (
										f.finding.evidence_refs && f.finding.evidence_refs.length > 0 ? (
											<Popover content={<EvidenceRefList refs={f.finding.evidence_refs} />} trigger="hover">
												<span style={{ fontSize: 11, cursor: "default" }}>
													{effectiveText}
												</span>
											</Popover>
										) : (
											<Text style={{ fontSize: 11, flex: 1 }}>{effectiveText}</Text>
										)
									)}
								</DismissibleItem>
							</div>
							<Text style={{ fontSize: 10, color: "var(--mg-text-secondary)" }}>
								<strong>Action: </strong>{f.recommended_action}
							</Text>
						</div>
					))}
				</div>
			</div>
		)}

		{analysisLimitations.length > 0 && (
			<div style={panelStyle}>
				<Text style={sectionLabel}>Analysis Limitations</Text>
				<div style={{ display: "flex", flexDirection: "column", gap: 3 }}>
					{analysisLimitations.map((lim, i) => (
						<DismissibleItem
							key={i}
							itemKey={`analysis_limitations:${i}`}
							decisions={decisions}
							onChange={onChange}
							isLocked={isLocked}
							defaultText={lim}
						>
							{(effectiveText) => (
								<Text style={{ fontSize: 11, color: "var(--mg-text-secondary)", display: "block" }}>
									• {effectiveText}
								</Text>
							)}
						</DismissibleItem>
					))}
				</div>
			</div>
		)}

		{incidentalFindings.length === 0 && analysisLimitations.length === 0 && (
			<Text style={{ fontSize: 11, color: "var(--mg-text-secondary)" }}>None recorded</Text>
		)}
	</div>
);

/* ─── Medication section ────────────────────────────────────────────────────── */

// itemKey uses the medication name as the stable identifier, matching this tab's
// existing rowKey — there's no other stable id on CurrentMedicationReview.
const currentMedicationItemKey = (r: CurrentMedicationReview) => `current_medication:${r.medication_name}`;

// Columns need to close over decisions/onChange/isLocked (to dim a dismissed row and
// render its Actions cell), so they're built per-render rather than as a module const.
const buildCurrentMedColumns = (
	decisions: ContentDecision[],
	onChange: (itemKey: string, patch: Partial<ContentDecision>) => void,
	isLocked: boolean,
) => {
	const isDismissed = (r: CurrentMedicationReview) =>
		getContentDecision(decisions, currentMedicationItemKey(r))?.dismissed ?? false;

	return [
		{
			title: "Medication",
			dataIndex: "medication_name",
			key: "medication_name",
			render: (v: string, r: CurrentMedicationReview) => (
				<Text style={{ fontSize: 11 }} type={isDismissed(r) ? "secondary" : undefined}>
					{v}
				</Text>
			),
		},
		{
			title: "Appropriateness",
			dataIndex: "appropriateness",
			key: "appropriateness",
			width: 130,
			render: (v: MedicationAppropriateness, r: CurrentMedicationReview) =>
				isDismissed(r) ? null : (
					<Tag color={APPROPRIATENESS_COLOR[v]} style={{ margin: 0, fontSize: 10 }}>{v.replace(/_/g, " ")}</Tag>
				),
		},
		{
			title: "Action",
			dataIndex: "recommended_action",
			key: "recommended_action",
			width: 110,
			render: (v: RecommendedAction, r: CurrentMedicationReview) =>
				isDismissed(r) ? null : <Tag style={{ margin: 0, fontSize: 10 }}>{RECOMMENDED_ACTION_LABEL[v]}</Tag>,
		},
		// Dismiss/restore is a manual button column (not DismissibleItem's render-prop) —
		// same reasoning as InvestigationsTab: a table row doesn't map cleanly onto it.
		// Hidden entirely once signed off, matching every other tab's isLocked behaviour.
		...(!isLocked ? [{
			title: "",
			key: "actions",
			width: 90,
			render: (_: unknown, r: CurrentMedicationReview) => {
				const itemKey = currentMedicationItemKey(r);
				return isDismissed(r) ? (
					<Button type="text" size="small" onClick={() => onChange(itemKey, { dismissed: false })}>
						Restore
					</Button>
				) : (
					<Button
						type="text"
						size="small"
						icon={<CloseOutlined />}
						onClick={() => onChange(itemKey, { dismissed: true })}
					/>
				);
			},
		}] : []),
	];
};

interface MedicationSectionProps {
	medAnalysis: MedicationAnalysis;
	decisions: MedicationDecision[];
	onDecisionChange: (medicationId: number, patch: Partial<MedicationDecision>) => void;
	isLocked: boolean;
}

const PgxPairsTable: React.FC<{ pairs: PgxPair[] }> = ({ pairs }) => {
	if (!pairs || pairs.length === 0) return null;
	const columns = [
		{ title: "Gene", dataIndex: "gene", key: "gene", width: 80, render: (v: string) => <Text style={{ fontSize: 10, fontWeight: 600 }}>{v}</Text> },
		{ title: "Variant", dataIndex: "variant", key: "variant", width: 90, render: (v: string | null) => v ? <Text style={{ fontSize: 10, fontFamily: "monospace" }}>{v}</Text> : null },
		{ title: "Source", dataIndex: "guideline_source", key: "guideline_source", width: 80, render: (v: string) => <Tag style={{ margin: 0, fontSize: 10 }}>{v}</Tag> },
		{ title: "Recommendation", dataIndex: "recommendation", key: "recommendation", render: (v: string) => <Text style={{ fontSize: 10 }}>{v}</Text> },
	];
	return (
		<Table
			dataSource={pairs}
			columns={columns}
			rowKey={(r) => `${r.gene}-${r.guideline_source}`}
			size="small"
			pagination={false}
		/>
	);
};

// Current Medications Review, extracted into its own tab. Row-level dismiss only (no
// inline edit) — this is a structured clinical comparison, not free AI narrative prose,
// same reasoning as InvestigationsTab and the genetic-risk-factors/methylation tables.
const CurrentMedicationsTab: React.FC<{
	review: CurrentMedicationReview[];
	decisions: ContentDecision[];
	onChange: (itemKey: string, patch: Partial<ContentDecision>) => void;
	isLocked: boolean;
}> = ({ review, decisions, onChange, isLocked }) => {
	// Signed-off + dismissed rows are dropped entirely, matching every other tab's rule.
	const items = review.filter(
		(r) => !(getContentDecision(decisions, currentMedicationItemKey(r))?.dismissed && isLocked),
	);

	return (
		<div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
			<Text style={sectionLabel}>Current Medications Review</Text>
			<Table<CurrentMedicationReview>
				dataSource={items}
				columns={buildCurrentMedColumns(decisions, onChange, isLocked)}
				rowKey={(r) => r.medication_name}
				size="small"
				scroll={{ x: true }}
				pagination={false}
				expandable={{
					expandedRowRender: (r) => (
						<div style={{ display: "flex", flexDirection: "column", gap: 10, padding: "4px 0" }}>
							{(r.dose || r.frequency || r.administration_instructions) && (
								<div>
									<Text style={sectionLabel}>Dosage &amp; Administration</Text>
									<div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
										{r.dose && <Text style={{ fontSize: 11 }}><strong>Dose:</strong> {r.dose}</Text>}
										{r.frequency && <Text style={{ fontSize: 11 }}><strong>Frequency:</strong> {r.frequency}</Text>}
										{r.administration_instructions && (
											<Text style={{ fontSize: 11 }}><strong>Administration:</strong> {r.administration_instructions}</Text>
										)}
									</div>
								</div>
							)}
							{r.duration_on_medication && (
								<div>
									<Text style={sectionLabel}>Duration</Text>
									<Text style={{ fontSize: 11 }}>{r.duration_on_medication}</Text>
								</div>
							)}
							{(r.indications?.length ?? 0) > 0 && (
								<div>
									<Text style={sectionLabel}>Indications</Text>
									<div style={{ display: "flex", flexDirection: "column", gap: 3 }}>
										{r.indications!.map((item, i) => (
											<div key={i} style={{ display: "flex", alignItems: "flex-start", gap: 6 }}>
												<Text style={{ fontSize: 11, lineHeight: "16px" }}>•</Text>
												<Text style={{ fontSize: 11 }}>{item}</Text>
											</div>
										))}
									</div>
								</div>
							)}
							{(r.contraindications?.length ?? 0) > 0 && (
								<div>
									<Text style={sectionLabel}>Contraindications</Text>
									<div style={{ display: "flex", flexDirection: "column", gap: 3 }}>
										{r.contraindications!.map((item, i) => (
											<div key={i} style={{ display: "flex", alignItems: "flex-start", gap: 6 }}>
												<Text type="danger" style={{ fontSize: 11, lineHeight: "16px" }}>•</Text>
												<Text style={{ fontSize: 11 }}>{item}</Text>
											</div>
										))}
									</div>
								</div>
							)}
							{((r.common_side_effects?.length ?? 0) > 0 || (r.rare_side_effects?.length ?? 0) > 0) && (
								<div>
									<Text style={sectionLabel}>Side Effects</Text>
									<div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
										{(r.common_side_effects?.length ?? 0) > 0 && (
											<div>
												<Text style={{ fontSize: 10, color: "var(--mg-text-secondary)", display: "block", marginBottom: 2 }}>Common</Text>
												<Text style={{ fontSize: 11 }}>{r.common_side_effects!.join("; ")}</Text>
											</div>
										)}
										{(r.rare_side_effects?.length ?? 0) > 0 && (
											<div>
												<Text style={{ fontSize: 10, color: "var(--mg-text-secondary)", display: "block", marginBottom: 2 }}>Rare</Text>
												<Text style={{ fontSize: 11 }}>{r.rare_side_effects!.join("; ")}</Text>
											</div>
										)}
									</div>
								</div>
							)}
							{r.review_frequency && (
								<div>
									<Text style={sectionLabel}>Review Frequency</Text>
									<Text style={{ fontSize: 11 }}>{r.review_frequency}</Text>
								</div>
							)}
							<div>
								<Text style={sectionLabel}>Clinical Notes</Text>
								<div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
									{r.genetic_interactions.length > 0 && (
										<Text style={{ fontSize: 11 }}><strong>Genetic Interactions:</strong> {r.genetic_interactions.join("; ")}</Text>
									)}
									<Text style={{ fontSize: 11 }}><strong>Clinical Notes:</strong> {r.clinical_notes}</Text>
									<Text style={{ fontSize: 11 }}><strong>Reasoning:</strong> {r.reasoning}</Text>
								</div>
							</div>
						</div>
					),
				}}
			/>
		</div>
	);
};

// Medications the deterministic safety screener force-dropped for an absolute contraindication.
// No Decision column and no Accept/Deny — a control here would let the practitioner undo the
// safety gate from the screen that's supposed to enforce it. `reason` is server-regenerated
// (red-flag label + catalogue contraindication label only) and safe to render; never log it.
const MedicationSafetyExclusionsTable: React.FC<{ exclusions: MedicationSafetyExclusion[] }> = ({ exclusions }) => {
	const columns = [
		{ title: "Medication", dataIndex: "medication_name", key: "medication_name", width: 180, render: (v: string) => <Text style={{ fontSize: 11, fontWeight: 600 }}>{v}</Text> },
		{
			title: "Excluded for",
			dataIndex: "red_flag_key",
			key: "red_flag_key",
			width: 220,
			render: (v: string) => <Tag color="red" style={{ margin: 0, fontSize: 10 }}>{RED_FLAG_LABEL[v] ?? v}</Tag>,
		},
		{ title: "Reason", dataIndex: "reason", key: "reason", render: (v: string) => <Text style={{ fontSize: 11 }}>{v}</Text> },
	];
	return (
		<Table
			dataSource={exclusions}
			columns={columns}
			rowKey={(r) => r.medication_id}
			size="small"
			scroll={{ x: true }}
			pagination={false}
		/>
	);
};

// Catalogue medications the analysis declined on clinical judgement — no hard contraindication.
// No Decision column and no Accept/Deny: a decision control here would create a prescribing path
// the analysis never justified. (Harry, answered: showing the rationale IS the requirement — a
// not-recommended medication is added manually via the prescribe task if the practitioner
// disagrees.)
//
// ⚠️ VISUALLY DISTINCT FROM MedicationSafetyExclusionsTable ON PURPOSE, AND NOT JUST FOR TONE.
// That table's `reason` is server-regenerated from fixed labels and PII-free by construction;
// this one's is MODEL-AUTHORED free text about a named patient with no server authority behind
// it. A practitioner has to be able to tell "the safety gate removed this" from "the model
// didn't pick this". Never log either field.
const NotRecommendedMedicationsTable: React.FC<{ medications: NotRecommendedMedication[] }> = ({ medications }) => {
	const columns = [
		{ title: "Medication", dataIndex: "medication_name", key: "medication_name", width: 180, render: (v: string) => <Text style={{ fontSize: 11, fontWeight: 600 }}>{v}</Text> },
		{ title: "Why not recommended", dataIndex: "reason", key: "reason", render: (v: string) => <Text style={{ fontSize: 11 }}>{v}</Text> },
	];
	return (
		<Table
			dataSource={medications}
			columns={columns}
			rowKey={(r) => r.medication_id}
			size="small"
			scroll={{ x: true }}
			pagination={false}
		/>
	);
};

// Read-only reference panel: the catalogue's OWN dosage_guides for this medication (clinician-
// authored data on the medications row), never the AI's generated dose/frequency/guidance.
// Fetched lazily via React Query — rc-table does not call `expandedRowRender` until a row has
// been expanded at least once (see rc-table's BodyRow.js `expandedRef`), so mounting this query
// here rather than eagerly in MedicationSection means opening the tab never fires one request
// per recommendation. No copy control: Harry, answer 2 — "never pre fill means the doctor has
// to type it in, we can only offer suggested dosages." This is text to read, not to apply.
const CatalogueDosagePanel: React.FC<{ medicationId: number }> = ({ medicationId }) => {
	const { data, isError } = useQuery({
		queryKey: ["catalogMedication", medicationId],
		queryFn: () => fetchCatalogMedication(medicationId),
	});

	// A lookup failure must never block the review — the dosage fields render regardless.
	if (isError) {
		return (
			<Text style={{ fontSize: 11, color: "var(--mg-text-secondary)" }}>
				Catalogue dosage guidance unavailable — proceed on clinical judgement.
			</Text>
		);
	}

	const guides = data?.dosage_guides;
	if (!guides || guides.length === 0) return null;

	return (
		<div>
			<Text style={sectionLabel}>Catalogue Dosage Guidance</Text>
			<div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
				{guides.map((g, i) => (
					<div key={i}>
						<div style={{ display: "flex", alignItems: "center", gap: 6 }}>
							<Text style={{ fontSize: 11, fontWeight: 600 }}>{g.label}</Text>
							{g.is_default && <Tag color="blue" style={{ margin: 0, fontSize: 10 }}>Default</Tag>}
						</div>
						<div style={{ display: "flex", flexDirection: "column", gap: 1, marginTop: 2 }}>
							<Text style={{ fontSize: 11 }}>{g.dosage}{g.frequency ? ` — ${g.frequency}` : ""}</Text>
							{g.administration_route && <Text style={{ fontSize: 11 }}>Route: {g.administration_route}</Text>}
							{g.instructions && <Text style={{ fontSize: 11 }}>{g.instructions}</Text>}
							{g.refills_authorized != null && <Text style={{ fontSize: 11 }}>Refills authorised: {g.refills_authorized}</Text>}
						</div>
					</div>
				))}
			</div>
		</div>
	);
};

const MedicationSection: React.FC<MedicationSectionProps> = ({ medAnalysis, decisions, onDecisionChange, isLocked }) => {
	const getDecision = (medicationId: number): MedicationDecision | undefined =>
		decisions.find((d) => d.medication_id === medicationId);

	const isAccepted = (rec: MedicationRecommendation): boolean | null =>
		getDecision(rec.medication_id)?.accepted ?? null;

	const recommendedMedColumns = [
		{
			title: "Priority",
			dataIndex: "priority",
			key: "priority",
			width: 75,
			render: (v: "high" | "medium" | "low") => (
				<Tag color={PRIORITY_COLOR[v]} style={{ margin: 0, fontSize: 10 }}>{v}</Tag>
			),
		},
		{
			title: "Medication",
			dataIndex: "medication_name",
			key: "medication_name",
			width: 240,
			// Secondary line surfaces the AI's rationale without requiring the row to be expanded.
			// This is the recommendation's own `rationale`, not the practitioner's editable copy in
			// the expanded row (`dec?.rationale`) — a read-only surfacing, not a move of that field.
			render: (v: string, record: MedicationRecommendation) => (
				<div>
					<Text style={{ fontSize: 11 }}>{v}</Text>
					{record.rationale && (
						<Text style={{ fontSize: 10, color: "var(--mg-text-secondary)", display: "block" }}>
							{record.rationale}
						</Text>
					)}
				</div>
			),
		},
		{
			title: "Decision",
			key: "decision",
			width: 120,
			render: (_: unknown, record: MedicationRecommendation) => {
				const accepted = isAccepted(record);
				if (isLocked) {
					if (accepted === null) return <Tag style={{ margin: 0, fontSize: 10 }}>Not Reviewed</Tag>;
					return <Tag color={accepted ? "green" : "red"} style={{ margin: 0, fontSize: 10 }}>{accepted ? "Accepted" : "Denied"}</Tag>;
				}
				return (
					<Space size={4}>
						<Button size="small" type={accepted === true ? "primary" : "default"} onClick={() => onDecisionChange(record.medication_id, { accepted: true })}>Accept</Button>
						<Button size="small" danger type={accepted === false ? "primary" : "default"} onClick={() => onDecisionChange(record.medication_id, { accepted: false })}>Deny</Button>
					</Space>
				);
			},
		},
	];

	const expandedRowContent = (r: MedicationRecommendation) => {
		const dec = getDecision(r.medication_id);
		// Dosage fields NEVER fall back to the AI's recommendation (r.X) — the practitioner types
		// every dose/frequency/dosage_guidance/administration_instructions themselves; the catalogue's
		// dosage_guides render alongside as read-only reference instead (CatalogueDosagePanel below).
		// indication/rationale/review_frequency are unaffected — clinical narrative the practitioner
		// edits, not a prescription instruction, and still seeded from the AI as before.
		const vals = {
			indication:                   dec?.indication                   ?? r.indication,
			dose:                         dec?.dose                         ?? "",
			frequency:                    dec?.frequency                    ?? "",
			dosage_guidance:              dec?.dosage_guidance              ?? "",
			administration_instructions: dec?.administration_instructions ?? "",
			rationale:                    dec?.rationale                    ?? r.rationale,
			review_frequency:            dec?.review_frequency            ?? r.review_frequency            ?? "",
		};
		// Renders one editable/locked field row; suppressed when locked (read-only) and empty so
		// older saved analyses that lack the new optional fields don't show a bold label over blank
		// text.
		const renderField = (key: keyof typeof vals, label: string, minRows: number) => {
			if (isLocked && !vals[key]) return null;
			return (
				<div key={key}>
					<Text style={{ fontSize: 10, color: "var(--mg-text-secondary)", display: "block", marginBottom: 2 }}>{label}</Text>
					{isLocked
						? <Text style={{ fontSize: 11 }}>{vals[key]}</Text>
						: <Input.TextArea value={vals[key]} onChange={(e) => onDecisionChange(r.medication_id, { [key]: e.target.value })} autoSize={{ minRows }} size="small" style={{ fontSize: 11 }} />
					}
				</div>
			);
		};
		const dosageFields: { key: keyof typeof vals; label: string; minRows: number }[] = [
			{ key: "dose",                         label: "Dose",                         minRows: 1 },
			{ key: "frequency",                    label: "Frequency",                    minRows: 1 },
			{ key: "dosage_guidance",              label: "Dosage Guidance",              minRows: 2 },
			{ key: "administration_instructions",  label: "Administration Instructions",  minRows: 2 },
		];
		return (
			<div style={{ display: "flex", flexDirection: "column", gap: 6, padding: "4px 0" }}>
				<div style={{ display: "grid", gridTemplateColumns: "1fr", gap: 8 }}>
					{renderField("indication", "Indication", 2)}
					<div>
						<Text style={sectionLabel}>Dosage &amp; Administration</Text>
						<div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
							{dosageFields.map(({ key, label, minRows }) => renderField(key, label, minRows))}
						</div>
					</div>
					<CatalogueDosagePanel medicationId={r.medication_id} />
					{renderField("rationale", "Rationale", 3)}
					{renderField("review_frequency", "Review Frequency", 1)}
					<div>
						<Text style={{ fontSize: 10, color: "var(--mg-text-secondary)", display: "block", marginBottom: 2 }}>Genetic Compatibility</Text>
						<Text style={{ fontSize: 11 }}>{r.genetic_compatibility.summary}</Text>
					</div>
					{r.contraindication_flags.length > 0 && (
						<div style={{ gridColumn: "1 / -1" }}>
							<Text style={{ fontSize: 10, color: "var(--mg-text-secondary)", display: "block", marginBottom: 3 }}>Contraindications</Text>
							<div style={{ display: "flex", flexDirection: "column", gap: 3 }}>
								{r.contraindication_flags.map((flag, i) => (
									<div key={i} style={{ display: "flex", alignItems: "flex-start", gap: 6 }}>
										<Text type="danger" style={{ fontSize: 11, lineHeight: "16px" }}>•</Text>
										<Text style={{ fontSize: 11 }}>{flag}</Text>
									</div>
								))}
							</div>
						</div>
					)}
					{(r.current_medication_interactions?.length ?? 0) > 0 && (
						<div style={{ gridColumn: "1 / -1" }}>
							<Text style={{ fontSize: 10, color: "var(--mg-text-secondary)", display: "block", marginBottom: 3 }}>Interactions with Current Medications &amp; Supplements</Text>
							<div style={{ display: "flex", flexDirection: "column", gap: 3 }}>
								{r.current_medication_interactions!.map((intx, i) => (
									<div key={i} style={{ display: "flex", alignItems: "flex-start", gap: 6 }}>
										<Tag
											color={intx.severity === "major" ? "error" : intx.severity === "moderate" ? "warning" : "default"}
											style={{ margin: 0, fontSize: 10, textTransform: "capitalize" }}
										>
											{intx.severity}
										</Tag>
										<Text style={{ fontSize: 11 }}>
											<strong>{intx.interacts_with}:</strong> {intx.interaction}
										</Text>
									</div>
								))}
							</div>
						</div>
					)}
					{((r.common_side_effects?.length ?? 0) > 0 || (r.rare_side_effects?.length ?? 0) > 0) && (
						<div style={{ gridColumn: "1 / -1" }}>
							<Text style={sectionLabel}>Side Effects</Text>
							<div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
								{(r.common_side_effects?.length ?? 0) > 0 && (
									<div>
										<Text style={{ fontSize: 10, color: "var(--mg-text-secondary)", display: "block", marginBottom: 2 }}>Common</Text>
										<Text style={{ fontSize: 11 }}>{r.common_side_effects!.join("; ")}</Text>
									</div>
								)}
								{(r.rare_side_effects?.length ?? 0) > 0 && (
									<div>
										<Text style={{ fontSize: 10, color: "var(--mg-text-secondary)", display: "block", marginBottom: 2 }}>Rare</Text>
										<Text style={{ fontSize: 11 }}>{r.rare_side_effects!.join("; ")}</Text>
									</div>
								)}
							</div>
						</div>
					)}
				</div>
				{r.genetic_compatibility.pgx_pairs.length > 0 && (
					<div>
						<Text style={{ ...sectionLabel, marginBottom: 4 }}>Pharmacogenomic Pairs</Text>
						<PgxPairsTable pairs={r.genetic_compatibility.pgx_pairs} />
					</div>
				)}
				{r.atc_code && (
					<Text style={{ fontSize: 10, color: "var(--mg-text-secondary)" }}>
						ATC: <span style={{ fontFamily: "monospace" }}>{r.atc_code}</span>
						{r.off_label && <Tag color="orange" style={{ marginLeft: 8, fontSize: 10 }}>Off-label</Tag>}
					</Text>
				)}
			</div>
		);
	};

	return (
		<div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
			<Text style={sectionLabel}>Recommendations from Catalog</Text>
			<Table<MedicationRecommendation>
				dataSource={byPriority(medAnalysis.recommendations)}
				columns={recommendedMedColumns}
				rowKey={(_, index) => index!}
				size="small"
				scroll={{ x: true }}
				pagination={false}
				expandable={{ expandedRowRender: expandedRowContent }}
			/>
		</div>
	);
};

/* ─── Content decisions (generic dismiss/edit state, one array per tab) ─────── */

// body_map_decisions is BodyMapView's — out of scope for this stage, still pending.
type ContentDecisionKey =
	| "genetic_decisions"
	| "diagnosis_decisions"
	| "clinical_decisions"
	| "current_medication_decisions"
	| "investigation_decisions"
	| "lifestyle_decisions"
	| "incidental_decisions";

const emptyContentDecisions: Record<ContentDecisionKey, ContentDecision[]> = {
	genetic_decisions:            [],
	diagnosis_decisions:          [],
	clinical_decisions:           [],
	current_medication_decisions: [],
	investigation_decisions:      [],
	lifestyle_decisions:          [],
	incidental_decisions:         [],
};

const contentDecisionsFrom = (review: PatientAnalysis["practitioner_review"]): Record<ContentDecisionKey, ContentDecision[]> => ({
	genetic_decisions:            review?.genetic_decisions            ?? [],
	diagnosis_decisions:          review?.diagnosis_decisions          ?? [],
	clinical_decisions:           review?.clinical_decisions           ?? [],
	current_medication_decisions: review?.current_medication_decisions ?? [],
	investigation_decisions:      review?.investigation_decisions      ?? [],
	lifestyle_decisions:          review?.lifestyle_decisions          ?? [],
	incidental_decisions:         review?.incidental_decisions         ?? [],
});

/* ─── Main renderer ─────────────────────────────────────────────────────────── */

const ComprehensiveAnalysisRenderer: React.FC<ComprehensiveAnalysisRendererProps> = ({
	analysisId,
	planId,
	onReviewed,
	preloadedAnalysis,
	showReviewButton = true,
	hideBodyMap = false,
	patientGender,
}) => {
	// Normalise the raw patient sex/gender string to what HumanBodySvg expects.
	const bodyGender: "male" | "female" = normaliseBodyGender(patientGender);
	const [decisions, setDecisions] = useState<MedicationDecision[]>(() => {
		const preloaded = preloadedAnalysis?.practitioner_review?.medication_decisions;
		if (preloaded?.length) return preloaded;
		const recs = preloadedAnalysis?.analysis_output?.medication_analysis?.recommendations ?? [];
		// Dosage fields (dose/frequency/dosage_guidance) start empty — never seeded from the AI's
		// recommendation. The practitioner types them; the catalogue's dosage_guides are offered as
		// read-only reference instead (CatalogueDosagePanel). indication/rationale are unaffected.
		return recs.map((r) => ({
			medication_id:         r.medication_id,
			accepted:              null,
			indication:            r.indication,
			dose:                  "",
			frequency:             "",
			dosage_guidance:       "",
			genetic_compatibility: r.genetic_compatibility.summary,
			rationale:             r.rationale,
		}));
	});
	const [contentDecisions, setContentDecisions] = useState<Record<ContentDecisionKey, ContentDecision[]>>(() => ({
		...emptyContentDecisions,
		...contentDecisionsFrom(preloadedAnalysis?.practitioner_review ?? null),
	}));
	const [isDirty, setIsDirty]         = useState(false);
	const [saving, setSaving]           = useState(false);
	const [signOffMode, setSignOffMode] = useState<null | "amendments" | "reject">(null);
	const [amendmentsText, setAmendmentsText] = useState("");
	const [signOffLoading, setSignOffLoading] = useState(false);
	const decisionsInitialized  = useRef(false);
	const prevAnalysisId        = useRef<number | undefined>(undefined);
	// The comprehensive analysis no longer embeds the body map — it comes from the
	// dedicated body-map store, falling back to any legacy embedded body_map below.
	const [dedicatedBodyMap, setDedicatedBodyMap] = useState<PatientBodyMap | null>(null);

	const { data: fetchedAnalysis, isLoading } = useQuery({
		queryKey: ["comprehensive-analysis", analysisId],
		queryFn: () => getPatientAnalysisById(analysisId!),
		enabled: !preloadedAnalysis && analysisId != null,
		refetchInterval: (query) => {
			const status = query.state.data?.status;
			return status && POLLING_STATUSES.includes(status) ? 5000 : false;
		},
	});

	const analysis = preloadedAnalysis ?? fetchedAnalysis;
	const isLoading_ = !preloadedAnalysis && isLoading;

	// Source the body map from the dedicated endpoint. A failed fetch silently
	// leaves dedicatedBodyMap null so the render falls back to the embedded
	// body_map (old analyses still render).
	useEffect(() => {
		if (hideBodyMap || !analysis?.patient_id) return;
		let cancelled = false;
		fetchPatientBodyMap(analysis.patient_id)
			.then((m) => { if (!cancelled) setDedicatedBodyMap(m); })
			.catch(() => { /* fall back to the embedded body_map below */ });
		return () => { cancelled = true; };
	}, [hideBodyMap, analysis?.patient_id]);

	useEffect(() => {
		if (prevAnalysisId.current !== analysisId) {
			decisionsInitialized.current = false;
			prevAnalysisId.current = analysisId;
			setIsDirty(false);
		}
		if (!preloadedAnalysis && fetchedAnalysis && !decisionsInitialized.current) {
			decisionsInitialized.current = true;
			setContentDecisions({
				...emptyContentDecisions,
				...contentDecisionsFrom(fetchedAnalysis.practitioner_review),
			});
			const preloaded = fetchedAnalysis.practitioner_review?.medication_decisions;
			if (preloaded?.length) {
				setDecisions(preloaded);
				return;
			}
			const recs = fetchedAnalysis.analysis_output?.medication_analysis?.recommendations ?? [];
			// Same rule as the useState initializer above — dosage fields start empty, never
			// seeded from the AI's recommendation.
			setDecisions(
				recs.map((r) => ({
					medication_id:         r.medication_id,
					accepted:              null,
					indication:            r.indication,
					dose:                  "",
					frequency:             "",
					dosage_guidance:       "",
					genetic_compatibility: r.genetic_compatibility.summary,
					rationale:             r.rationale,
				})),
			);
		}
	}, [fetchedAnalysis, preloadedAnalysis, analysisId]);

	// Once the practitioner has signed off (accepted the suggestions and completed the step), the
	// medication decisions are final — lock the accept/deny + dose/frequency/guidance fields so the
	// recorded clinical decision can no longer be changed.
	const reviewFinalised =
		analysis?.review_status === "approved" ||
		analysis?.review_status === "approved_with_amendments" ||
		analysis?.review_status === "rejected";
	const editingLocked = !showReviewButton || reviewFinalised;

	const handleDecisionChange = (medicationId: number, patch: Partial<MedicationDecision>) => {
		setDecisions((prev) => {
			const exists = prev.find((d) => d.medication_id === medicationId);
			if (exists) {
				return prev.map((d) => d.medication_id === medicationId ? { ...d, ...patch } : d);
			}
			const rec = (analysis?.analysis_output?.medication_analysis?.recommendations ?? []).find(
				(r) => r.medication_id === medicationId,
			);
			// Dosage fields start empty here too — never seeded from the AI's recommendation. `...patch`
			// still applies after, so if this create was itself triggered by the practitioner typing
			// into a dosage field, that typed value (not "") is what lands.
			return [
				...prev,
				{
					medication_id:         medicationId,
					accepted:              null,
					indication:            rec?.indication ?? "",
					dose:                  "",
					frequency:             "",
					dosage_guidance:       "",
					genetic_compatibility: rec?.genetic_compatibility.summary ?? "",
					rationale:             rec?.rationale ?? "",
					...patch,
				},
			];
		});
		setIsDirty(true);
	};

	const handleContentDecisionChange = (key: ContentDecisionKey, itemKey: string, patch: Partial<ContentDecision>) => {
		setContentDecisions((prev) => {
			const list = prev[key];
			const exists = list.some((d) => d.item_key === itemKey);
			const updated = exists
				? list.map((d) => (d.item_key === itemKey ? { ...d, ...patch } : d))
				: [...list, { item_key: itemKey, ...patch }];
			return { ...prev, [key]: updated };
		});
		setIsDirty(true);
	};

	const handleSaveDecisions = async () => {
		setSaving(true);
		try {
			await savePractitionerReview(planId, { medication_decisions: decisions, ...contentDecisions });
			setIsDirty(false);
		} catch {
			message.error("Failed to save decisions.");
		} finally {
			setSaving(false);
		}
	};

	const handleSignOff = async (
		reviewStatus: "approved" | "approved_with_amendments" | "rejected",
		amendments?: string,
	) => {
		if (!analysis) return;

		// Guard: all medication suggestions must be reviewed before approving
		if (reviewStatus !== "rejected") {
			const recs = analysis.analysis_output?.medication_analysis?.recommendations ?? [];
			if (recs.length > 0) {
				const unreviewedCount = recs.filter((r) => {
					const dec = decisions.find((d) => d.medication_id === r.medication_id);
					return dec === undefined || dec.accepted === null;
				}).length;
				if (unreviewedCount > 0) {
					Modal.warning({
						title: "Medication Review Required",
						content: `${unreviewedCount} suggested medication${unreviewedCount > 1 ? "s have" : " has"} not been reviewed. Please accept or deny each suggested medication before signing off.`,
					});
					return;
				}
			}
		}

		setSignOffLoading(true);
		try {
			await signOffAnalysis(analysis.id, { review_status: reviewStatus, practitioner_amendments: amendments });
			setSignOffMode(null);
			setAmendmentsText("");
			if (reviewStatus !== "rejected") {
				onReviewed?.();
			} else {
				message.info("Analysis rejected. The treatment plan stage has not advanced.");
			}
		} catch {
			message.error("Failed to submit sign-off.");
		} finally {
			setSignOffLoading(false);
		}
	};

	if (isLoading_ || !analysis) return <ProcessingState />;
	if (analysis.status === "pending" || analysis.status === "processing") return <ProcessingState />;
	// Appraisal gate: don't show the analysis until every section has been
	// validated against the patient's real data.
	if (analysis.status === "appraising") return <ProcessingState message="Validating analysis — checking findings against the patient's data…" />;
	if (analysis.status === "failed") return <FailedState message={analysis.error_message} />;

	const isMedicationPending = analysis.status === "stage_1_complete";

	const output = analysis.analysis_output;
	if (!output) return <FailedState message="Analysis output missing." />;

	const {
		executive_summary: exec,
		genetic_profile,
		clinical_assessment,
		investigations,
		lifestyle_assessment,
		body_map,
		medication_analysis,
		confirmed_diagnoses = [],
		clinical_considerations = [],
		genetic_risk_factors = [],
		incidental_findings = [],
		analysis_limitations = [],
		requires_urgent_review = false,
		urgent_findings = [],
	} = output;

	// Investigations can arrive as a non-array; genetics findings live in the Genetic tab.
	const investigationItems = (Array.isArray(investigations?.investigations) ? investigations.investigations : [])
		.filter((inv) => (inv.order_type as string) !== "genetic");

	const canSignOff = showReviewButton &&
		analysis.trigger_source === "treatment_plan" &&
		(analysis.review_status === "awaiting_review" || analysis.review_status === "under_review");

	const hasGeneticContent =
		(genetic_profile?.key_findings?.length ?? 0) > 0 ||
		!!genetic_profile?.methylation_assessment ||
		(genetic_profile?.summary?.length ?? 0) > 0 ||
		(genetic_profile?.conventional_genetic_summary?.length ?? 0) > 0;

	const hasDiagnosesContent =
		confirmed_diagnoses.length > 0 ||
		clinical_considerations.length > 0 ||
		genetic_risk_factors.length > 0;

	const hasIncidentalContent =
		incidental_findings.length > 0 || analysis_limitations.length > 0;

	// How to present medication recommendations: when the practitioner requested them but none were
	// returned, tell them plainly (don't hide the tab); when present, flag the tab for attention.
	const medReview = medicationReviewState(analysis);
	const noRecsDoNotProceed = medReview.showNoRecsWarning && analysis.trigger_source === "treatment_plan";

	return (
		<div style={{ display: "flex", flexDirection: "column", gap: 10, minWidth: 0, overflowX: "clip" }}>
			{/* Urgent findings banner */}
			{requires_urgent_review && urgent_findings.length > 0 && (
				<Alert
					type="error"
					icon={<WarningOutlined />}
					showIcon
					message="Urgent Review Required"
					description={
						<div style={{ display: "flex", flexDirection: "column", gap: 4, marginTop: 4 }}>
							{urgent_findings.map((f, i) => (
								<div key={i} style={{ display: "flex", alignItems: "center", gap: 6 }}>
									<Tag color="red" style={{ margin: 0, fontSize: 10 }}>{f.severity}</Tag>
									<Tag style={{ margin: 0, fontSize: 10 }}>{f.recommended_timeframe.replace(/_/g, " ")}</Tag>
									<Text style={{ fontSize: 12 }}>{f.finding}</Text>
								</div>
							))}
						</div>
					}
				/>
			)}

			{/* Medication analysis */}
			{isMedicationPending && (
				<div style={{ ...panelStyle, display: "flex", alignItems: "center", gap: 8 }}>
					<Spin size="small" />
					<Text style={{ fontSize: 12, color: "var(--mg-text-secondary)" }}>Analysing medications…</Text>
				</div>
			)}

			{/* No medications recommended — advise the practitioner not to proceed (plan context only) */}
			{noRecsDoNotProceed && (
				<Alert
					type="warning"
					icon={<WarningOutlined />}
					showIcon
					message="No medications recommended for this patient"
					description={medReview.suppressedForSafety
						? "No medications could be recommended for this patient. One or more candidates were excluded on safety grounds (absolute contraindication). Do not proceed without clinical review."
						: "The analysis completed and did not recommend any medications. Do not proceed with this treatment plan."}
				/>
			)}

			{/* Practitioner sign-off */}
			{canSignOff && (
				<div style={{ ...panelStyle, border: "1px solid var(--mg-warning, #fa8c16)" }}>
					<Text style={{ ...sectionLabel, color: "var(--mg-warning, #fa8c16)", marginBottom: 8 }}>
						Practitioner Sign-off Required
					</Text>

					{signOffMode === null && (
						<Space>
							<Popconfirm
								title={medReview.hasRecommendations ? "Approve Analysis" : "Approve & Cancel Plan"}
								description={medReview.hasRecommendations
									? "Confirm you have reviewed the analysis and it is clinically appropriate. The treatment plan will advance."
									: "No medications were recommended. Approving will cancel this treatment plan."}
								onConfirm={() => handleSignOff("approved")}
								okText={medReview.hasRecommendations ? "Approve" : "Approve & Cancel"}
								cancelText="Cancel"
							>
								<Button type="primary" size="small" loading={signOffLoading}>
									{medReview.hasRecommendations ? "Approve" : "Approve & Cancel Plan"}
								</Button>
							</Popconfirm>
							<Button size="small" onClick={() => setSignOffMode("amendments")}>
								Approve with Amendments
							</Button>
							<Button size="small" danger onClick={() => setSignOffMode("reject")}>
								Reject
							</Button>
						</Space>
					)}

					{signOffMode === "amendments" && (
						<div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
							<Text style={{ fontSize: 11 }}>Record any amendments or modifications before approving:</Text>
							<Input.TextArea
								value={amendmentsText}
								onChange={(e) => setAmendmentsText(e.target.value)}
								placeholder="Describe any amendments or clinical modifications…"
								autoSize={{ minRows: 2, maxRows: 6 }}
								style={{ fontSize: 12 }}
							/>
							<Space>
								<Button
									type="primary"
									size="small"
									loading={signOffLoading}
									disabled={!amendmentsText.trim()}
									onClick={() => handleSignOff("approved_with_amendments", amendmentsText)}
								>
									Approve with Amendments
								</Button>
								<Button size="small" onClick={() => { setSignOffMode(null); setAmendmentsText(""); }}>
									Cancel
								</Button>
							</Space>
						</div>
					)}

					{signOffMode === "reject" && (
						<div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
							<Text style={{ fontSize: 11 }}>Provide a reason for rejection (optional):</Text>
							<Input.TextArea
								value={amendmentsText}
								onChange={(e) => setAmendmentsText(e.target.value)}
								placeholder="Reason for rejection…"
								autoSize={{ minRows: 2, maxRows: 4 }}
								style={{ fontSize: 12 }}
							/>
							<Space>
								<Button
									size="small"
									danger
									loading={signOffLoading}
									onClick={() => handleSignOff("rejected", amendmentsText || undefined)}
								>
									Confirm Rejection
								</Button>
								<Button size="small" onClick={() => { setSignOffMode(null); setAmendmentsText(""); }}>
									Cancel
								</Button>
							</Space>
						</div>
					)}
				</div>
			)}

			{/* Body map */}
			{!hideBodyMap && (
				<BodyMapView bodyMap={dedicatedBodyMap ?? body_map} gender={bodyGender} />
			)}

			{/* Detail tabs */}
			<Tabs
				size="small"
				style={{ marginTop: 0 }}
				// Pin the tab nav to the top of the scrolling drawer body while reading
				// long tab content. Solid card background + z-index keep content from
				// showing through or overlapping the pinned bar.
				tabBarStyle={{
					position: "sticky",
					top: 0,
					zIndex: 2,
					background: "var(--mg-bg-card, var(--mg-bg))",
				}}
				items={[
					...(hasGeneticContent ? [{
						key: "genetic",
						label: "Genetic",
						children: (
							<GeneticTab
								profile={genetic_profile}
								decisions={contentDecisions.genetic_decisions}
								onChange={(itemKey, patch) => handleContentDecisionChange("genetic_decisions", itemKey, patch)}
								isLocked={editingLocked}
							/>
						),
					}] : []),
					...(hasDiagnosesContent ? [{
						key: "diagnoses",
						label: "Diagnoses & Risks",
						children: (
							<DiagnosesRisksTab
								confirmedDiagnoses={confirmed_diagnoses}
								clinicalConsiderations={clinical_considerations}
								geneticRiskFactors={genetic_risk_factors}
								// Every source record the diagnosis drill-down fetches is verified against
								// this id before it renders — see DiagnosisSourcePanel's security note.
								patientId={analysis.patient_id}
								decisions={contentDecisions.diagnosis_decisions}
								onChange={(itemKey, patch) => handleContentDecisionChange("diagnosis_decisions", itemKey, patch)}
								isLocked={editingLocked}
							/>
						),
					}] : []),
					{
						key: "clinical",
						label: "Clinical",
						children: (
							<ClinicalTab
								assessment={clinical_assessment}
								executive={exec}
								decisions={contentDecisions.clinical_decisions}
								onChange={(itemKey, patch) => handleContentDecisionChange("clinical_decisions", itemKey, patch)}
								isLocked={editingLocked}
							/>
						),
					},
					...(medication_analysis && !isMedicationPending && (medication_analysis.current_medications_review?.length ?? 0) > 0 ? [{
						key: "medications",
						label: "Medications",
						children: (
							<CurrentMedicationsTab
								review={medication_analysis.current_medications_review}
								decisions={contentDecisions.current_medication_decisions}
								onChange={(itemKey, patch) => handleContentDecisionChange("current_medication_decisions", itemKey, patch)}
								isLocked={editingLocked}
							/>
						),
					}] : []),
					{
						key:      "investigations",
						label:    (
							<span>
								Investigations
								{investigationItems.length > 0 && (
									<Badge
										count={investigationItems.length}
										size="small"
										style={{ marginLeft: 6 }}
									/>
								)}
							</span>
						),
						children: (
							<InvestigationsTab
								patientId={analysis.patient_id}
								investigations={investigationItems}
								decisions={contentDecisions.investigation_decisions}
								onChange={(itemKey, patch) => handleContentDecisionChange("investigation_decisions", itemKey, patch)}
								isLocked={editingLocked}
							/>
						),
					},
					{
						key: "lifestyle",
						label: "Lifestyle",
						children: (
							<LifestyleTab
								lifestyle={lifestyle_assessment}
								decisions={contentDecisions.lifestyle_decisions}
								onChange={(itemKey, patch) => handleContentDecisionChange("lifestyle_decisions", itemKey, patch)}
								isLocked={editingLocked}
							/>
						),
					},
					...(hasIncidentalContent ? [{
						key: "incidental",
						label: "Incidental",
						children: (
							<IncidentalTab
								incidentalFindings={incidental_findings}
								analysisLimitations={analysis_limitations}
								decisions={contentDecisions.incidental_decisions}
								onChange={(itemKey, patch) => handleContentDecisionChange("incidental_decisions", itemKey, patch)}
								isLocked={editingLocked}
							/>
						),
					}] : []),
					...(medReview.showSuggestedTab && medication_analysis ? [{
						key: "suggested_medications",
						label: (
							<span>
								Suggested Medications
								{medReview.hasRecommendations && (
									<WarningOutlined style={{ marginLeft: 6, color: "var(--mg-warning, #faad14)" }} />
								)}
							</span>
						),
						children: (
							<>
								{medReview.safetyScreenFailed && (
									<Alert
										type="error"
										showIcon
										message="Medication safety screening did not complete"
										description="Automated medication safety screening did not complete for this analysis. The recommendations below have not been screened for absolute contraindications and require explicit clinical verification before prescribing."
										style={{ marginBottom: 12 }}
									/>
								)}
								{/* Not approved — excluded on safety grounds. Sits outside the hasRecommendations
								    fork below on purpose: when every candidate was suppressed, that's exactly when
								    the practitioner most needs to see the names, not less. No Accept/Deny here —
								    a decision control on a medication the safety gate removed would undo the gate. */}
								{medReview.safetyExclusions.length > 0 && (
									<div style={{ ...panelStyle, marginBottom: 12, border: "1px solid var(--mg-error, #ff4d4f)" }}>
										<Text style={{ ...sectionLabel, color: "var(--mg-error, #ff4d4f)" }}>
											Not approved — excluded on safety grounds
										</Text>
										<Text style={{ fontSize: 11, display: "block", marginBottom: 8 }}>
											The deterministic safety screener withheld these medications for an absolute
											contraindication. This exclusion cannot be overridden from this screen.
										</Text>
										<MedicationSafetyExclusionsTable exclusions={medReview.safetyExclusions} />
									</div>
								)}

								{medReview.hasRecommendations && (
									<div style={panelStyle}>
										<MedicationSection
											medAnalysis={medication_analysis}
											decisions={decisions}
											onDecisionChange={handleDecisionChange}
											isLocked={editingLocked}
										/>
									</div>
								)}

								{/* Not recommended for this patient — the THIRD list (ClickUp 86d3vnw2f).
								    Clinical judgement, no hard contraindication, so informational in tone and
								    deliberately quieter than the safety-exclusions block above.

								    OUTSIDE the hasRecommendations fork, like Phase 1's block and for the same
								    reason: a catalogue where everything was declined is exactly when the
								    practitioner most needs to read why. LAST, so the tab reads
								    strongest-warning -> actionable -> informational.

								    No Accept/Deny — a decision control here would create a prescribing path
								    the analysis never justified. Absent OR empty renders nothing at all: no
								    empty section, no placeholder. */}
								{(medReview.notRecommended?.length ?? 0) > 0 && (
									<div style={{ ...panelStyle, marginTop: 12 }}>
										<Text style={sectionLabel}>Not recommended for this patient</Text>
										<Text style={{ fontSize: 11, display: "block", marginBottom: 8 }}>
											Catalogue medications that are neither recommended nor excluded on safety
											grounds for this patient. No absolute contraindication applies. A reason may
											be the analysis&apos; own or a placeholder recorded where none was given.
										</Text>
										<NotRecommendedMedicationsTable medications={medReview.notRecommended ?? []} />
									</div>
								)}
							</>
						),
					}] : []),
				]}
			/>

			{/* Save pending decisions — covers medication decisions and every tab's dismiss/edit
			    content decisions, so it's reachable regardless of which tab is active or whether
			    the Suggested Medications tab is shown at all (e.g. no medication recommendations
			    were requested for this analysis). */}
			{!editingLocked && isDirty && (
				<div style={{ display: "flex", justifyContent: "flex-end" }}>
					<Button size="small" loading={saving} onClick={handleSaveDecisions}>
						Save Decisions
					</Button>
				</div>
			)}

			
			{/* Reviewed state indicator */}
			{showReviewButton && !canSignOff && (
				analysis.review_status === "approved" || analysis.review_status === "approved_with_amendments"
			) && (
				<div style={{ display: "flex", justifyContent: "flex-end", paddingTop: 4 }}>
					<Tag color="green">
						<CheckCircleOutlined style={{ marginRight: 4 }} />
						{REVIEW_STATUS_TAG[analysis.review_status].label}
						{analysis.reviewed_at && ` — ${dayjs(analysis.reviewed_at).format("D MMM YYYY")}`}
					</Tag>
				</div>
			)}
		</div>
	);
};

export default ComprehensiveAnalysisRenderer;
